/**
 * Подсветка лучших и худших в таблице «Эффективность операторов».
 *
 * Ранжируются только показатели качества — конверсия и чек-лист. «Звонки», этапы воронки и
 * «Жалобы» — объёмы: по ним «худшим» оказался бы просто маленький салон.
 *
 * Правило одно на оба показателя:
 * - участвуют строки с достаточными данными (флаги сервера `lowSample` / `checklistLowSample`) и
 *   числовым значением; остальные не подсвечиваются никогда — у них отметка «Недостаточно данных»;
 * - выделяется до трёх с каждой стороны, но не больше трети участников: середина таблицы всегда
 *   остаётся без цвета, иначе при пяти строках зелёными и красными были бы почти все;
 * - ничья на границе из подсветки выпадает, а не раздувает её: если четвёртый равен третьему,
 *   ни один из них не «лучший». Все равны — не подсвечен никто.
 *
 * Подсветка считается по всем строкам таблицы и от сортировки не зависит.
 */
import {
  hasEnoughChecklistData,
  hasEnoughConversionData,
  readFiniteNumber
} from './employeeEfficiency';

/** Сколько строк выделять с каждой стороны, самое большее. */
export const HIGHLIGHT_LIMIT = 3;

export const EFFICIENCY_RANKED_METRICS = Object.freeze({
  conversion: {
    read: (row) => row?.conversionRate,
    hasEnoughData: hasEnoughConversionData,
    subject: 'по конверсии',
    insufficient: { calls: 'применимых звонков', metric: 'конверсия' }
  },
  checklist: {
    read: (row) => row?.avgFirstOperatorTotal,
    hasEnoughData: hasEnoughChecklistData,
    subject: 'по чек-листу',
    insufficient: { calls: 'звонков с оценкой чек-листа', metric: 'средний балл' }
  }
});

/**
 * `Map` строка → `'best' | 'worst'`. Ключ — сам объект строки: сортировка переставляет те же
 * объекты, и `employeeId` у строки может не быть.
 */
export const rankEfficiencyHighlights = (rows, metricKey) => {
  const highlights = new Map();
  const metric = EFFICIENCY_RANKED_METRICS[metricKey];

  if (!metric || !Array.isArray(rows)) {
    return highlights;
  }

  const ranked = rows
    .filter((row) => metric.hasEnoughData(row))
    .map((row) => ({ row, value: readFiniteNumber(metric.read(row)) }))
    .filter((entry) => entry.value !== null)
    .sort((left, right) => right.value - left.value);

  const count = Math.min(HIGHLIGHT_LIMIT, Math.floor(ranked.length / 3));

  if (count === 0) {
    return highlights;
  }

  // Строго больше первого невыделяемого и строго меньше последнего невыделяемого: так ничья
  // на границе выпадает, а лучшие и худшие не пересекаются по построению.
  const bestBoundary = ranked[count].value;
  const worstBoundary = ranked[ranked.length - 1 - count].value;

  ranked.forEach(({ row, value }) => {
    if (value > bestBoundary) {
      highlights.set(row, 'best');
    } else if (value < worstBoundary) {
      highlights.set(row, 'worst');
    }
  });

  return highlights;
};

/** Оформление: зелёный и красный той же пары, что изменение KPI в шапке (`Dashboard.jsx`). */
export const EFFICIENCY_HIGHLIGHT_STYLES = Object.freeze({
  best: {
    className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300',
    label: 'Лучший результат'
  },
  worst: {
    className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    label: 'Худший результат'
  }
});

/** Значение без подсветки — нейтральное: цвет в этой таблице означает только место в рейтинге. */
export const NEUTRAL_VALUE_CLASS = 'text-foreground';

export const INSUFFICIENT_DATA_LABEL = 'Недостаточно данных';

/** Подсказка у подсвеченного значения: что значит цвет. */
export const describeHighlight = (metricKey, highlight) => {
  const style = EFFICIENCY_HIGHLIGHT_STYLES[highlight];
  const metric = EFFICIENCY_RANKED_METRICS[metricKey];

  if (!style || !metric) {
    return undefined;
  }

  return `${style.label} ${metric.subject} среди сотрудников с достаточным объёмом звонков`;
};

/** Подсказка у отметки «Недостаточно данных»: почему строку не сравнивают. */
export const describeInsufficientData = (metricKey, threshold) => {
  const metric = EFFICIENCY_RANKED_METRICS[metricKey];

  if (!metric) {
    return undefined;
  }

  const { calls, metric: subject } = metric.insufficient;
  const amount = threshold ? `Менее ${threshold} ${calls}` : `Мало ${calls}`;

  return `${amount} — ${subject} не участвует в сравнении лучших и худших`;
};
